import { Check, CircleDashed, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { PlutoAnswerMarkdown } from "./PlutoAnswerMarkdown";
import { PlutoOutreachDraftCard } from "./PlutoOutreachDraftCard";
import type { PlutoChatMessage } from "./PlutoProvider";
import { plutoToolLabel, plutoWaitingDetail, plutoWaitingHeadline } from "./tool-status";

type PlutoResearchThreadProps = {
  messages: readonly PlutoChatMessage[];
  isPending?: boolean;
  /** The tool the server last reported starting, or null while Pluto thinks. */
  activeToolName?: string | null;
  /** Every tool this turn has reached, in order, the last one still running. */
  toolSteps?: readonly string[];
  /** The answer so far. Superseded by the stored turn once it arrives. */
  streamingText?: string;
  /** The model's reasoning so far, shown folded away because it is not the answer. */
  reasoningText?: string;
  error?: string | null;
};

/** Long enough to read, short enough that a slow turn still looks alive. */
const PHRASE_ROTATION_MS = 3_000;

const useRotatingPhrase = (isPending: boolean) => {
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    if (!isPending) {
      setRotation(0);
      return;
    }

    const timer = setInterval(() => setRotation((current) => current + 1), PHRASE_ROTATION_MS);
    return () => clearInterval(timer);
  }, [isPending]);

  return rotation;
};

export const PlutoResearchThread = ({
  messages,
  isPending = false,
  activeToolName = null,
  toolSteps = [],
  streamingText = "",
  reasoningText = "",
  error = null,
}: PlutoResearchThreadProps) => {
  const rotation = useRotatingPhrase(isPending);
  const isWriting = isPending && streamingText.length > 0;

  return (
    <div className="space-y-5">
      {messages.map((message) => {
        if (message.role === "user") {
          return (
            <div
              className="ml-auto max-w-[88%] rounded-2xl rounded-br-md bg-stone-900 px-4 py-3 text-sm leading-6 text-white"
              key={message.id}
            >
              {message.text}
            </div>
          );
        }

        return (
          // A turn that wrote outreach says so in prose and hands over the copy
          // below it, so the card is read as part of the answer rather than as
          // something that replaced one.
          <div className="space-y-3" key={message.id}>
            <div className="max-w-[92%] rounded-2xl rounded-bl-md border border-stone-300 bg-white px-4 py-4">
              <PlutoAnswerMarkdown text={message.text} />
              {message.stopped ? (
                <p className={`text-xs font-medium text-stone-500${message.text.trim().length > 0 ? " mt-2" : ""}`}>
                  {message.text.trim().length > 0
                    ? "You stopped this answer."
                    : "You stopped this before Pluto answered."}
                </p>
              ) : null}
              {message.toolNames.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-1.5 border-t border-stone-100 pt-3">
                  {message.toolNames.map((name) => (
                    <span
                      className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-medium text-stone-600"
                      key={name}
                    >
                      {plutoToolLabel(name)}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
            {message.draft ? <PlutoOutreachDraftCard draft={message.draft} /> : null}
          </div>
        );
      })}

      {isWriting ? (
        <div
          aria-live="polite"
          className="max-w-[92%] rounded-2xl rounded-bl-md border border-stone-300 bg-white px-4 py-4"
        >
          <PlutoAnswerMarkdown text={streamingText} />
        </div>
      ) : isPending ? (
        // Progress is not an answer, so it is not dressed as one. It sits on the
        // background until there is something written to put in a bubble.
        <div aria-live="polite" className="max-w-[92%] px-1">
          <div className="flex items-center gap-2 text-sm font-medium text-stone-600">
            <CircleDashed aria-hidden="true" className="h-4 w-4 animate-spin" />
            {plutoWaitingHeadline(activeToolName, rotation)}
          </div>
          {toolSteps.length > 0 ? (
            <ol className="mt-2 space-y-1">
              {toolSteps.map((name, index) => {
                const isRunning = index === toolSteps.length - 1;

                return (
                  <li
                    className={`flex items-center gap-2 text-xs leading-5 ${
                      isRunning ? "text-stone-600" : "text-stone-400"
                    }`}
                    key={`${name}-${String(index)}`}
                  >
                    {isRunning ? (
                      <Loader2 aria-hidden="true" className="h-3 w-3 animate-spin" />
                    ) : (
                      <Check aria-hidden="true" className="h-3 w-3" />
                    )}
                    {plutoToolLabel(name)}
                  </li>
                );
              })}
            </ol>
          ) : (
            <p className="mt-2 text-xs leading-5 text-stone-500">{plutoWaitingDetail(activeToolName)}</p>
          )}
          {reasoningText ? (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs font-medium text-stone-500">Thinking</summary>
              <p className="mt-2 whitespace-pre-wrap text-xs leading-5 text-stone-500">{reasoningText}</p>
            </details>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <div className="max-w-[92%] rounded-2xl rounded-bl-md border border-red-200 bg-red-50 px-4 py-4">
          <p className="text-sm font-medium text-red-900">Pluto could not answer</p>
          <p className="mt-1 text-xs leading-5 text-red-700">{error}</p>
        </div>
      ) : null}

      {messages.length === 0 && !isPending && !error ? (
        <div className="max-w-[92%] rounded-2xl rounded-bl-md border border-dashed border-stone-300 bg-white px-4 py-4">
          <div className="mb-2 flex items-center gap-2 text-xs font-medium text-stone-700">
            <CircleDashed aria-hidden="true" className="h-3.5 w-3.5" />
            Waiting for Pluto
          </div>
          <p className="text-sm leading-6 text-stone-600">
            Ask a question and Pluto will keep answering in this thread.
          </p>
        </div>
      ) : null}
    </div>
  );
};
